import { useMemo } from "react";
import { firstBy } from "thenby";
import { Workout } from "features/Workouts/Workouts.types";
import useSelectedEntities from "providers/SelectedEntitiesProvider/useSelectedEntities";
import useSearchText from "providers/SearchTextProvider/useSearchText";
import useTableSort from "providers/TableSortProvider/useTableSort";
import useEntityTextSearch from "hooks/useEntityTextSearch";
import { Athlete } from "types/athlete";
import { Group } from "types/groups";
import useFilterByAthletesOrGroups from "hooks/useFilterByAthletesOrGroups";
import useSelectedWorkoutLabels from "providers/SelectedWorkoutLabelsProvider/useSelectedWorkoutLabels";
import useCollator from "hooks/useCollator/useCollator";
import useAuthorName from "hooks/useAuthorName";
import ArrayHelpers from "helpers/ArrayHelpers";
import useNaturalSort from "hooks/useNaturalSort";

const searchableProperties: (keyof Workout)[] = ["name"];

function useWorkoutsFilter(workouts: Workout[]) {
  const naturalCompare = useNaturalSort();
  const sortBy = useTableSort();
  const searchText = useSearchText();
  const selectedAthletesAndGroups = useSelectedEntities<Athlete | Group>();
  const selectedWorkoutLabels = useSelectedWorkoutLabels();
  const searchedWorkouts = useEntityTextSearch(searchText, workouts, searchableProperties);
  const collator = useCollator();
  const workoutsFilteredByAthleteOrGroups = useFilterByAthletesOrGroups(selectedAthletesAndGroups, searchedWorkouts);
  const workoutsFilteredByLabels = useMemo(() => {
    if (!selectedWorkoutLabels.length) return workoutsFilteredByAthleteOrGroups;

    return workoutsFilteredByAthleteOrGroups.filter((workout) =>
      selectedWorkoutLabels.every((label) => workout.labels.includes(label.name))
    );
  }, [workoutsFilteredByAthleteOrGroups, selectedWorkoutLabels]);

  // Get unique creator IDs and resolve author names
  const creatorIds = useMemo(() => {
    const ids = workoutsFilteredByLabels.map((workout) => workout.creatorId);
    const filteredIds = ids.filter((id): id is string => Boolean(id));
    return ArrayHelpers.removeDuplicates(filteredIds);
  }, [workoutsFilteredByLabels]);

  const creators = useAuthorName(creatorIds);

  /**
   * We don't sort by natural sort order due to a management decision to keep the way it is now.
   * If that changes in the future, we can easily switch to useNaturalSort here.
   */
  const sortedWorkouts = useMemo(() => {
    return workoutsFilteredByLabels.slice(0).sort(
      sortBy.key === "name"
        ? firstBy("name", { cmp: collator.compare, ignoreCase: true, direction: sortBy.direction })
        : firstBy((a: Workout, b: Workout) => {
            if (sortBy.key === "author") {
              const aName = a.creatorId ? creators[a.creatorId] || "" : "";
              const bName = b.creatorId ? creators[b.creatorId] || "" : "";
              if (aName && !bName) return -1;
              if (!aName && bName) return 1;
              return sortBy.direction === "asc" ? naturalCompare(aName, bName) : naturalCompare(bName, aName);
            }
            return 0;
          }).thenBy(sortBy.key, { cmp: collator.compare, ignoreCase: true, direction: sortBy.direction })
    );
  }, [workoutsFilteredByLabels, sortBy.key, sortBy.direction, collator.compare, creators, naturalCompare]);

  return sortedWorkouts;
}

export default useWorkoutsFilter;
